import { faExternalLink, faRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileInput } from '@partful/features/file-uploader/ui/FileInput/FileInput';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useUploadFile } from '@partful/shared/lib/hooks/useUploadFile';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import { WizardPageContent, WizardPageFooter, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { GETCADJobResponse, productInventoryEndpoints } from '@services/productInventory';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import { useCreateCADJob } from './lib/hooks/useCreateCADJob';
import { uploadCADFormSchema, UploadCADFormSchema } from './lib/UploadCADFormSchema';
import { UploadFailed } from './ui/UploadFailed/UploadFailed';
import { UploadProgress } from './ui/UploadProgress/UploadProgress';
import styles from './UploadCAD.module.scss';

type UploadDetails = {
  runID: string;
  productName: string;
  cadFileName: string;
};

export default function UploadCAD() {
  const [uploadDetails, setUploadDetails] = useState<UploadDetails | null>(null);

  const { accountId } = useTenant();
  const { t: i18n } = useTranslation();

  const { handleSubmit, control, formState, reset } = useForm<UploadCADFormSchema>({
    resolver: zodResolver(uploadCADFormSchema),
    mode: 'onChange',
    defaultValues: {
      productName: '',
      file: null,
    },
  });

  const {
    mutateAsync: uploadFile,
    isSuccess: isCADUploadComplete,
    isLoading: isCADUploadLoading,
    isError: isCADUploadFailed,
    cancelUpload,
    progress,
    reset: resetUpload,
  } = useUploadFile();
  const { mutateAsync: createCADJob, isLoading: isCreateCADJobLoading } = useCreateCADJob();

  const onSubmit = useCallback(
    async (data: UploadCADFormSchema): Promise<void> => {
      const { productName, file } = data;

      const { uploadURL, runID } = await createCADJob({ accountId, productName });
      setUploadDetails({ runID, productName, cadFileName: file!.name });

      await uploadFile({ file: file!, uploadURL });
    },
    [accountId, createCADJob, uploadFile]
  );

  const canFetchCADInfo = Boolean(uploadDetails?.runID) && isCADUploadComplete;

  const {
    data: cadJobData,
    isSuccess: isCADInfoSuccess,
    isFetching: isCADInfoLoading,
    isError: isCADInfoError,
  } = useProductInventoryQuery<GETCADJobResponse>(
    `${productInventoryEndpoints.contentProcessing.cad}/${uploadDetails?.runID}`,
    {
      account: accountId,
    },
    {
      enabled: canFetchCADInfo,
    }
  );

  const isCADInvalid = isCADInfoSuccess && (isCADInfoError || !cadJobData || !(cadJobData.fileDetails.length > 0));

  const resetFailedUpload = () => {
    reset();
    resetUpload();
    setUploadDetails(null);
  };

  if (isCADInfoSuccess && !isCADUploadFailed && !isCADInvalid) {
    return (
      <Redirect
        to={{
          pathname: `/create-product/cad/general-assembly`,
          search: `?runID=${uploadDetails?.runID}&productName=${uploadDetails?.productName}`,
          state: {
            successMessage: {
              title: i18n('createProduct:fileUploaded'),
              body: i18n('createProduct:fileUploadedSuccessfully', { filename: uploadDetails?.cadFileName }),
            },
          },
        }}
      />
    );
  }

  if (isCADUploadFailed || isCADInvalid) {
    return (
      <WizardPageLayout>
        <WizardPageContent>
          <UploadFailed
            isFailedUpload={isCADUploadFailed}
            filename={uploadDetails?.cadFileName!}
            productName={uploadDetails?.productName!}
            onConfirm={resetFailedUpload}
          />
        </WizardPageContent>
      </WizardPageLayout>
    );
  }

  if (isCADUploadLoading || isCADInfoLoading) {
    return (
      <WizardPageLayout>
        <WizardPageContent>
          <UploadProgress
            filename={uploadDetails?.cadFileName!}
            percentage={progress?.percentage!}
            remainingTime={progress?.remainingTime!}
          />
        </WizardPageContent>
        <WizardPageFooter>
          <Button onClick={cancelUpload} size="large" variant="tertiary">
            Cancel Upload
          </Button>
        </WizardPageFooter>
      </WizardPageLayout>
    );
  }

  return (
    <WizardPageLayout>
      <WizardPageContent className={styles.formContainer}>
        <form id="cadForm" name="cadForm" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formControl}>
            <Typography as="label" htmlFor="productName">
              {i18n('createProduct:cad.productName')} <sup>({i18n('createProduct:common.required')})</sup>
            </Typography>
            <Controller
              name="productName"
              control={control}
              render={({ field: { name, onBlur, onChange, value, disabled } }) => (
                <Input
                  id={name}
                  placeholder={i18n('createProduct:cad.productNamePlaceholder')}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  disabled={disabled}
                />
              )}
            />
          </div>
          <div className={styles.formControl}>
            <Typography as="label" htmlFor="file">
              {i18n('createProduct:cad.cadFile')} <sup>({i18n('createProduct:common.zipFileRequired')})</sup>
            </Typography>
            <FileInput control={control} accept=".zip" placeholder={i18n('createProduct:cad.cadFilePlaceholder')} />
            <Button
              variant="secondary"
              size="small"
              href="https://partful.io/knowledge/accepted-file-formats-for-uploads"
              target="_blank"
              rel="noreferrer"
            >
              <span>
                {i18n('createProduct:cad.acceptedCAD')} <FontAwesomeIcon icon={faExternalLink} />
              </span>
            </Button>
          </div>
        </form>
      </WizardPageContent>
      <WizardPageFooter>
        <span /> {/* to make the button right aligned */}
        <Button
          type="submit"
          form="cadForm"
          size="large"
          disabled={!formState.isValid || formState.isSubmitting || isCreateCADJobLoading}
          data-testid="upload-btn"
        >
          {i18n('createProduct:cad.startUpload')}
          <FontAwesomeIcon icon={faRight} />
        </Button>
      </WizardPageFooter>
    </WizardPageLayout>
  );
}
